import type { ClassLevel } from './class';

export const FEE_TYPES = ['REGISTRATION', 'TUITION', 'CANTEEN', 'TRANSPORT', 'OTHER'] as const;
export type FeeType = (typeof FEE_TYPES)[number];

export function isFeeType(value: string): value is FeeType {
  return (FEE_TYPES as readonly string[]).includes(value);
}

/** French display labels — cosmetic only, never sent to/received from the backend
 * (SCH-04, same pattern as CLASS_LEVEL_LABELS in class.ts). */
export const FEE_TYPE_LABELS: Record<FeeType, string> = {
  REGISTRATION: 'Inscription',
  TUITION: 'Scolarité',
  CANTEEN: 'Cantine',
  TRANSPORT: 'Transport',
  OTHER: 'Autre',
};

// UX-58 — `Invoice.period` (`String?`) est stocké au format `YYYY-MM`
// (`apps/worker/src/lib/enrollmentInvoicing.ts#formatMonthLabel`,
// `${year}-${paddedMonth}`, TOUJOURS 4 chiffres + tiret + 2 chiffres — seul
// écrivain réel de cette colonne à ce jour, ENR-05/TUITION ; REGISTRATION et
// les autres types de frais restent TOUJOURS `null`, aucune notion de mois).
// Un pur défaut d'AFFICHAGE trouvé sur ~11 écrans différents (web-admin ET
// web-parent) qui affichaient déjà `feeType` (parfois brut, parfois déjà
// traduit) sans JAMAIS montrer `period` — ou en l'affichant BRUT
// ("Scolarité — 2026-09") — rendant deux factures de mois DIFFÉRENTS
// indiscernables d'un vrai doublon en un coup d'œil (trouvaille SCH-31/36).

/**
 * `'2026-09'` -> `'Septembre 2026'`. `null`/format inattendu -> `null` (le
 * cas `null` DOIT rester fréquent — REGISTRATION/CANTEEN/TRANSPORT/OTHER
 * n'ont jamais de période — l'appelant décide comment composer le résultat,
 * JAMAIS "Inscription — null"/une chaîne vide affichée). Un format inattendu
 * retombe sur la valeur BRUTE plutôt qu'un crash ou un masquage silencieux —
 * même discipline `?? valeur brute` déjà établie partout ailleurs dans ce
 * projet pour un enum/une valeur non reconnue.
 */
export function formatInvoicePeriodLabel(period: string | null): string | null {
  if (!period) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const date = new Date(Date.UTC(year, monthIndex, 1));
  // `timeZone: 'UTC'` — même précaution que la construction du `Date` lui-même
  // (`Date.UTC`), pour ne jamais laisser l'environnement d'exécution (fuseau
  // du navigateur/du process Node) glisser le mois affiché d'un jour à
  // l'autre bout du mois — même piège déjà documenté pour `pg`/ENR-05.
  const label = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * `('TUITION', '2026-09')` -> `'Scolarité — Septembre 2026'` ;
 * `('REGISTRATION', null)` -> `'Inscription'` (jamais de tiret/texte de
 * repli quand `period` est absent). `feeType` accepté en `string` brute (les
 * réponses API le typent souvent ainsi) — repli sur la valeur brute si
 * inconnue, même convention `FEE_TYPE_LABELS[feeType] ?? feeType` déjà
 * établie partout ailleurs dans ce projet. Fonction de composition CANONIQUE
 * pour toute ligne de facture affichée — réutilisée par CHAQUE écran qui
 * liste des factures (Caisse/Historique des encaissements/Factures impayées/
 * StudentFinancialOverview/Payments web-parent/résolution de doublons...),
 * jamais un second calcul dupliqué par écran.
 */
export function describeInvoiceFeeTypeWithPeriod(feeType: string, period: string | null): string {
  const feeTypeLabel = FEE_TYPE_LABELS[feeType as FeeType] ?? feeType;
  const periodLabel = formatInvoicePeriodLabel(period);
  return periodLabel ? `${feeTypeLabel} — ${periodLabel}` : feeTypeLabel;
}

// PAY-10 — mode de calcul d'un type de réduction. Label uniquement (le modèle
// complet `DiscountType` vit dans `@monecolier/api-client`, aligné sur la
// forme RÉELLE renvoyée par `GET /discount-types`, apps/api).
export const DISCOUNT_MODES = ['FIXED', 'PERCENTAGE'] as const;
export type DiscountMode = (typeof DISCOUNT_MODES)[number];

export const DISCOUNT_MODE_LABELS: Record<DiscountMode, string> = {
  FIXED: 'Montant fixe',
  PERCENTAGE: 'Pourcentage',
};

export const FEE_RECURRENCES = ['ONE_TIME', 'MONTHLY', 'PER_TERM', 'ANNUAL'] as const;
export type FeeRecurrence = (typeof FEE_RECURRENCES)[number];

export function isFeeRecurrence(value: string): value is FeeRecurrence {
  return (FEE_RECURRENCES as readonly string[]).includes(value);
}

export const FEE_RECURRENCE_LABELS: Record<FeeRecurrence, string> = {
  ONE_TIME: 'Ponctuel',
  MONTHLY: 'Mensuel',
  PER_TERM: 'Par trimestre',
  ANNUAL: 'Annuel',
};

/** A fee schedule for a school year/fee type, optionally restricted to one class
 * level (`level: null` means "applies to every level").
 *
 * SCH-04: `schoolYearId` REPLACES the earlier free-text `schoolYear` (FND-02) — a
 * real FK to SchoolYear (SCH-11), same rework as SchoolClass.
 *
 * No single UNIQUE(tenantId, schoolYearId, feeType, level) constraint at the DB
 * level: Postgres treats two NULLs as distinct in a plain UNIQUE constraint, so it
 * wouldn't catch two `level: null` rows for the same (tenantId, schoolYearId,
 * feeType) — enforced instead by two partial unique indexes (NULL / NOT NULL cases
 * handled separately). See apps/api/prisma/schema.prisma's comment on this model
 * for the full detail and a real Prisma pitfall around it.
 *
 * ⚠️ Once an Invoice references (tenantId, schoolYear.label, feeType), that
 * fee type's grid is LOCKED for the school year — no further create/update allowed
 * (409 `fee_configuration_locked`), regardless of `level`. See apps/api/src/lib/
 * feeConfiguration.ts#isFeeTypeLocked. */
export interface FeeConfiguration {
  id: string;
  tenantId: string;
  schoolYearId: string;
  feeType: FeeType;
  amount: number;
  recurrence: FeeRecurrence;
  level: ClassLevel | null;
  createdAt: string;
  updatedAt: string;
}

export const INVOICE_STATUSES = ['PENDING', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export function isInvoiceStatus(value: string): value is InvoiceStatus {
  return (INVOICE_STATUSES as readonly string[]).includes(value);
}

export const PAYMENT_METHODS = ['CASH', 'BANK_TRANSFER', 'MOBILE_MONEY', 'CARD', 'CHEQUE'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export function isPaymentMethod(value: string): value is PaymentMethod {
  return (PAYMENT_METHODS as readonly string[]).includes(value);
}

export const PAYMENT_STATUSES = ['PENDING', 'COMPLETED', 'FAILED', 'REFUNDED'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export function isPaymentStatus(value: string): value is PaymentStatus {
  return (PAYMENT_STATUSES as readonly string[]).includes(value);
}

/** A bill issued for a student, tied to a specific enrollment. */
export interface Invoice {
  id: string;
  tenantId: string;
  enrollmentId: string;
  studentId: string;
  feeType: FeeType;
  period: string | null;
  amountDue: number;
  amountPaid: number;
  dueDate: string;
  status: InvoiceStatus;
  createdAt: string;
  updatedAt: string;
}

/** A payment received against an invoice. `idempotencyKey` is UNIQUE at the DB level
 * — a retried submission must never be recorded twice. */
export interface Payment {
  id: string;
  tenantId: string;
  invoiceId: string;
  amount: number;
  method: PaymentMethod;
  status: PaymentStatus;
  idempotencyKey: string;
  collectedBy: string | null;
  externalRef: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A receipt issued for a payment — strict 1-1 (a payment has at most one receipt).
 * Considered immutable once issued (see `contentHash`): no `updatedAt`. */
export interface Receipt {
  id: string;
  tenantId: string;
  paymentId: string;
  receiptNumber: string;
  pdfUrl: string;
  contentHash: string;
  issuedAt: string;
}
